import React, { createContext, useState, useContext, useEffect } from 'react';
import { userAPI } from '../services/api';
import { getUserLocation } from '../utils/locationUtils';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [userLocation, setUserLocation] = useState(null);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const response = await userAPI.getMe();
          setUser(response.data.user);
          // Update localStorage with fresh user data
          localStorage.setItem('user', JSON.stringify(response.data.user));

          // Capture location for customers
          if (response.data.user.role === 'customer') {
            try {
              const location = await getUserLocation();
              setUserLocation(location);
            } catch (error) {
              console.log('Location access denied or unavailable:', error.message);
            }
          }
        } catch (error) {
          console.error('Error fetching user:', error);
          localStorage.removeItem('token');
          setToken(null);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, [token]);

  const login = async (loginId, password) => {
    try {
      // Send loginId as both email and phone - backend will check both
      const response = await userAPI.login({
        email: loginId,
        phone: loginId,
        password
      });

      if (response.data.success) {
        const { token: newToken, user: userData } = response.data;
        localStorage.setItem('token', newToken);
        localStorage.setItem('user', JSON.stringify(userData));
        setToken(newToken);
        setUser(userData);
        return { success: true };
      } else {
        return {
          success: false,
          message: response.data.message || 'Login failed'
        };
      }
    } catch (error) {
      console.error('Login API error:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Login failed'
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await userAPI.register(userData);
      const { token: newToken, user: newUser } = response.data;

      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(newUser);

      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Registration failed'
      };
    }
  };

  // Login with OTP - stores token and user after OTP verification
  const loginWithOtp = (newToken, userData) => {
    localStorage.setItem('token', newToken);
    localStorage.setItem('user', JSON.stringify(userData));
    setToken(newToken);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
  };

  // Refresh user data from server
  const refreshUser = async () => {
    try {
      const response = await userAPI.getMe();
      setUser(response.data.user);
    } catch (error) {
      console.error('Error refreshing user:', error);
    }
  };

  // Update travels info
  const updateTravelsInfo = async (travelsData) => {
    try {
      const response = await userAPI.updateTravelsInfo(travelsData);
      // Update user state with new travelsInfo
      setUser(prev => ({
        ...prev,
        travelsInfo: response.data.travelsInfo
      }));
      return { success: true, travelsInfo: response.data.travelsInfo };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update travels info'
      };
    }
  };

  // Upload travels logo
  const uploadTravelsLogo = async (formData) => {
    try {
      const response = await userAPI.uploadTravelsLogo(formData);
      // Update user state with new logo
      setUser(prev => ({
        ...prev,
        travelsInfo: {
          ...prev.travelsInfo,
          logo: response.data.logo
        }
      }));
      return { success: true, logo: response.data.logo };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to upload logo'
      };
    }
  };

  const value = {
    user,
    token,
    loading,
    userLocation,
    setUserLocation,
    login,
    loginWithOtp,
    register,
    logout,
    refreshUser,
    updateTravelsInfo,
    uploadTravelsLogo,
    isAuthenticated: !!user,
    // Convenience: direct access to travelsInfo
    travelsInfo: user?.travelsInfo || null
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
